import React, { useEffect, useRef, useState, useCallback } from 'react';
import './App.scss';
import Header from './components/Header';
import Footer from './components/Footer';
import HowItWorksModal from './components/ServiceDescription';
import PopupModal from './components/PopupModal';
import EntrancePopup from './components/EntrancePopup';
import ImageGrid from './components/ImageGrid';
import PortraitDetailsModal from './components/PortraitDetailsModal';
// Replaced direct heavy canvas mount with lazy loader to avoid competing with initial image decode
import LazyThemeBackground from './components/LazyThemeBackground';
import { redirectToStripeCheckout } from './utils/stripeUtils';
import { trackEvent } from './utils/analytics';
import { getCurrentChannel } from './config';

import MemoryRipples from './components/MemoryRipples';
import useFetchUsers from './hooks/useFetchUsers';
import Notification from './components/Notification';
// EntrancePopup removed
import UserLimitBanner from './components/UserLimitBanner';
import useCustomFetchApi from './utils/customFetchApi';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import ThanksPage from './components/ThanksPage';
import LegalPage from './components/LegalPage';
import { useDispatch, useSelector } from 'react-redux';
import { setPortraitUUID } from './store/sharedStateSlice';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminProfiles from './components/admin/AdminProfiles';
import AdminChannels from './components/admin/AdminChannels';
import AdminProfileEditPage from './components/admin/AdminProfileEditPage';
import AdminBulkUpload from './components/admin/AdminBulkUpload';
import AdminProfileFixer from './components/admin/AdminProfileFixer';
import AdminPaymentLinks from './components/admin/AdminPaymentLinks';
import AdminSpotPrices from './components/admin/AdminSpotPrices';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import MemoryWall from './pages/MemoryWall';

function AppContent() {
  const { isAuthenticated } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobileInitial = typeof window !== 'undefined' ? window.innerWidth <= 768 : false;
  const [isMobile, setIsMobile] = useState(isMobileInitial);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const containerWidth = isMobile ? 127 : 200;
  const containerHeight = isMobile ? 127 : 200;
  const limit = isMobile ? 18 : 40;
  const [countryList, setCountryList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isHowItWorksVisible, setIsHowItWorksVisible] = useState(false);
  const [selectedPortrait, setSelectedPortrait] = useState(null);
  const observer = useRef();
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [isParticipateShown, setIsParticipateShown] = useState(true);
  const [prefilledData, setPrefilledData] = useState(null);
  const [processedPortraits, setProcessedPortraits] = useState(new Set()); // Track processed portraits
  const [configs, setConfigs] = useState(null);
  // Entrance popup disabled (kept flags for minimal invasive change)
  const [showEntrancePopup, setShowEntrancePopup] = useState(false);
  const [entrancePopupGateMode, setEntrancePopupGateMode] = useState(false);
  const [highlightGateRows, setHighlightGateRows] = useState(false);
  const [isHeaderCompact, setIsHeaderCompact] = useState(false);
  const [hasGateActivated, setHasGateActivated] = useState(false); // Track if gate threshold has been reached (unauth third scroll)
  const [lastPopupCloseTime, setLastPopupCloseTime] = useState(0); // Track when popup was last closed
  const [lastGateLaterTime, setLastGateLaterTime] = useState(0); // Track when user clicked Maybe Later in gate mode
  const GATE_LATER_COOLDOWN_MS = 2 * 60 * 1000; // 2 minutes cooldown for gate popup after clicking Maybe Later
  // Gate popup frequency control: show only every few bottom scrolls after activation
  const GATE_SCROLLS_THRESHOLD = 3; // show the gate popup every 3rd bottom reach
  const GATE_ATTEMPT_DEBOUNCE_MS = 1000; // avoid double counts from multiple listeners
  const gateBottomHitCountRef = useRef(0);
  const lastGateAttemptTimeRef = useRef(0);
  // Grace-delayed global spinner to avoid flashing overlay for very fast requests
  const [showMainSpinner, setShowMainSpinner] = useState(false);
  const { step, portraitUUID, STEPS, generalError, generalErrorMessage, isLoadingMain, imageErrorMessage } = useSelector(state => state.sharedState);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // Detect language from URL and set i18n + document direction
  useEffect(() => {
    const firstSegment = (location.pathname || '').split('/')[1] || '';
    const availableLangs = ['en', 'my', 'ar']; // Use explicit list instead of dynamic
    let lang = 'en';
    if (firstSegment && availableLangs.includes(firstSegment)) {
      lang = firstSegment;
    }

    if (i18n.language !== lang) {
      i18n.changeLanguage(lang).then(() => {
        // Update title after language change is complete
        updateDocumentTitle();
      });
    } else {
      // Language is already correct, just update title
      updateDocumentTitle();
    }

    // Update document title based on route and language
    function updateDocumentTitle() {
      const pathSegments = location.pathname.split('/').filter(Boolean);
      let titleKey = 'documentTitle.default';

      // Check if on payment/thanks page
      if (location.pathname.includes('/payment/')) {
        titleKey = 'documentTitle.thanks';
      } else if (pathSegments.includes('iraq')) {
        titleKey = 'documentTitle.iraq';
      } else if (pathSegments.includes('malaysia')) {
        titleKey = 'documentTitle.malaysia';
      }

    }

    // Set current channel attr for potential debugging without console
    const currentChannel = getCurrentChannel();
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-channel', currentChannel);
    }

    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    }
  }, [location.pathname, i18n, t]);

  // Handle profile URL - redirect to landing page and open modal
  useEffect(() => {
    const handleProfileUrl = async () => {
      const pathSegments = location.pathname.split('/').filter(Boolean);

      // Check if URL matches pattern: /lang/theme/profile/uuid or /profile/uuid
      let profileIndex = pathSegments.indexOf('profile');
      if (profileIndex !== -1 && pathSegments[profileIndex + 1]) {
        const profileUuid = pathSegments[profileIndex + 1];

        // Extract lang and channel from current URL
        const lang = pathSegments[0] && ['en', 'ar', 'my'].includes(pathSegments[0]) ? pathSegments[0] : 'en';
        // For profile URLs, use the channel from the URL structure
        let channel = null;
        if (profileIndex === 2) {
          // Pattern: /lang/channel/profile/uuid (e.g., /en/malaysia/profile/uuid)
          channel = pathSegments[1];
        }

        // Construct the new URL without profile part
        const newPath = channel ? `/${lang}/${channel}` : `/${lang}`;

        // Navigate immediately so the route matches and the grid renders
        navigate(newPath, { replace: true });

        try {
          //          console.log('🔍 Profile URL handler: Making API call to /user/' + profileUuid);
          // Fetch the profile data - match the same API call pattern as handlePortraitClick
          const userDetails = await api.get(`/user/${profileUuid}`, { isLoadingMain: true, showNotification: true });
          //          console.log('✅ Profile URL handler: API response received', userDetails);
          if (userDetails) {
            // Safely handle date parsing
            let joined = 'Unknown';
            if (userDetails.created_at) {
              try {
                const date = new Date(userDetails.created_at);
                if (!isNaN(date.getTime())) {
                  joined = date.toLocaleDateString();
                }
              } catch (dateError) {
                console.warn('Invalid date format for created_at:', userDetails.created_at);
              }
            }

            // Update image paths in the grid if they've changed (like in handlePortraitClick)
            if (userDetails.images && userDetails.images.length > 0) {
              updateImagePaths(profileUuid, userDetails.images);
            }

            // Set the selected portrait to open the modal
            setSelectedPortrait({
              uuid: profileUuid,
              ...userDetails,
              joined,
            });
          }
        } catch (error) {
          console.error('Error loading profile from URL:', error);
          // Already navigated to landing; nothing else to do here
        }
      }
    };

    // Only run if the URL actually contains a profile
    if (location.pathname.includes('/profile/')) {
      handleProfileUrl();
    }
  }, [location.pathname, navigate]);

  // Localhost auto-redirect disabled: stay on the current route during local development

  // Disable auto-redirect to avoid delaying initial paint; render LP immediately
  // Previously redirected to /{lang}/malaysia

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: '', type: '' }), 4000);
  };

  const api = useCustomFetchApi(showNotification);

  // Spinner grace delay logic (Point 1 optimization): only show after 250ms of sustained loading
  useEffect(() => {
    let timer;
    if (isLoadingMain) {
      if (!showMainSpinner) {
        timer = setTimeout(() => setShowMainSpinner(true), 250); // delay overlay
      }
    } else {
      setShowMainSpinner(false); // hide immediately when loading ends
    }
    return () => timer && clearTimeout(timer);
  }, [isLoadingMain, showMainSpinner]);
  // Gate handler: don't show EntrancePopup if the submission modal is open
  const handleGate = useCallback(() => {
    // Only highlight; do not open entrance popup
    setHighlightGateRows(true);
    setHasGateActivated(true);
  }, []);

  const { images, fetchUsers, isLoading, hasMore, prependImage, updateImagePaths } = useFetchUsers(limit, api, {
    onGate: handleGate,
  });

  // Cold start: temporarily disable backdrop blurs to reduce first composite GPU cost
  useEffect(() => {
    const body = document.body;
    if (!body.classList.contains('cold-start')) {
      body.classList.add('cold-start');
    }
    const timeout = setTimeout(() => body.classList.remove('cold-start'), 1200); // fallback removal
    if (images.length > 0) {
      body.classList.remove('cold-start');
      clearTimeout(timeout);
    }
    return () => {
      body.classList.remove('cold-start');
      clearTimeout(timeout);
    };
  }, [images.length]);


  const fetchUserAndPrefillForm = async () => {
    const userData = await api.get(`/user/${portraitUUID}`, { isLoadingMain: true, showNotification: true });

    let prefilledData = {
      nickname: userData.nickname || '',
      email: userData.email || '',
      message: userData.message || '',
      country: userData.country || '',
      errorCode: generalError,
      errorMessage: generalErrorMessage,
      images: userData.images || []
    };

    if (prefilledData.country) {
      setSelectedCountry(prefilledData.country);
    }

    setPrefilledData(prefilledData);
    if (prefilledData.errorMessage) {
      showNotification(prefilledData.errorMessage, 'error');
    }

    showPopup();
  };


  useEffect(() => {
    if (portraitUUID && step === STEPS.PAYMENT_SUCCESS_NO_ISSUE) {
      showNewlyCreatedPortrait(portraitUUID);

      // Clear portraitUUID after successful completion so next upload creates new portrait
      setTimeout(() => {
        dispatch(setPortraitUUID(null));
      }, 2000); // Give time for the success flow to complete
    }

    if (portraitUUID && step === STEPS.PAYMENT_SUCCESS_ISSUE && !processedPortraits.has(portraitUUID)) {
      setProcessedPortraits(prev => new Set(prev).add(portraitUUID));
      fetchUserAndPrefillForm(portraitUUID);
    }

  }, [portraitUUID, step, dispatch]);

  useEffect(() => {
    if (imageErrorMessage) {
      showNotification(imageErrorMessage, 'error');
    }
  }, [imageErrorMessage]);

  useEffect(() => {
    // Skip network during prerender
    if (typeof navigator !== 'undefined' && /HeadlessChrome/.test(navigator.userAgent)) return;
    api.get('/countries')
      .then(data => {
        if (Array.isArray(data.countries)) {
          setCountryList(data.countries);
          if (data.selected?.code) {
            setSelectedCountry(data.selected.code);
          }
        }
      })
      .catch(err => console.error('❌ Error loading countries:', err));
  }, []);

  // Fetch configs based on current language (previously Malaysia-only; now enabled for all channels)
  useEffect(() => {
    // Skip network during prerender
    if (typeof navigator !== 'undefined' && /HeadlessChrome/.test(navigator.userAgent)) return;

    const currentChannel = getCurrentChannel();
    console.log('Current channel:', currentChannel); // Debug log

    const currentLang = i18n.language || 'en';
    console.log('Fetching configs for lang:', currentLang); // Debug log
    api.get(`/configs?lang=${currentLang}`)
      .then(data => {
        console.log('Configs received:', data); // Debug log
        setConfigs(data.configs);
        // Entrance popup disabled: ignore any config flags
      })
      .catch(err => console.error('❌ Error loading configs:', err));
  }, [i18n.language]); // Only depend on language, not pathname

  // If the form opens, make sure any EntrancePopup is hidden to avoid overlay conflicts
  useEffect(() => {
    if (isPopupVisible && showEntrancePopup) {
      setShowEntrancePopup(false);
      setEntrancePopupGateMode(false);
    }
  }, [isPopupVisible]);

  // Use ref to avoid dependency issues
  const fetchUsersRef = useRef(fetchUsers);
  useEffect(() => {
    fetchUsersRef.current = fetchUsers;
  }, [fetchUsers]);
  // Debounce for repeated bottom popup triggers (scroll-based only)
  const lastBottomPopupTimeRef = useRef(0);
  const BOTTOM_POPUP_COOLDOWN_MS = 10000; // require 10s between bottom-triggered popups

  const handleEntrancePopupClose = useCallback((reason) => {
    const now = Date.now();
    setShowEntrancePopup(false);
    setEntrancePopupGateMode(false);
    setLastPopupCloseTime(now);
    if (reason === 'later') {
      setLastGateLaterTime(now);
    }
  }, []);

  const lastImageRef = useCallback(node => {
    if (isLoading || !hasMore) return;

    let timeout;
    if (observer.current) observer.current.disconnect();

    // Scroll-bottom popup should not be blocked by localStorage cooldown (only entrance/config uses that)

    // Mobile-specific intersection observer settings
    const isMobile = window.innerWidth <= 768;
    const rootMargin = isMobile ? '50px' : '200px';
    const threshold = isMobile ? 0.5 : 0.01;

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !timeout) {
        const now = Date.now();
        // Do not show any popup from the intersection observer anymore; only use bottom scroll detection
        timeout = setTimeout(() => {
          fetchUsersRef.current();
          timeout = null;
        }, isMobile ? 100 : 300);
      }
    }, {
      root: isMobile ? document.querySelector('.image-grid-container') : null,
      rootMargin,
      threshold,
    });

    if (node) {
      observer.current.observe(node);
    }
  }, [isLoading, hasMore, hasGateActivated, lastPopupCloseTime, showEntrancePopup, isPopupVisible]);

  // Additional bottom reach detection (independent of intersection staying active) after gate activation
  useEffect(() => {
    // Show the subscribe gate only when unauthenticated, after 30+ profiles, at the true bottom
    let ticking = false;
    let prevNear = false;
    const NEAR_PX = 0; // Require exact bottom
    const check = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const gridContainer = document.querySelector('.image-grid-container');
        let scrollTop, scrollHeight, clientHeight;
        if (gridContainer && window.innerWidth <= 768) {
          scrollTop = gridContainer.scrollTop;
          scrollHeight = gridContainer.scrollHeight;
          clientHeight = gridContainer.clientHeight;
        } else {
          scrollTop = window.scrollY || document.documentElement.scrollTop;
          scrollHeight = document.documentElement.scrollHeight;
          clientHeight = window.innerHeight;
        }
        const near = (scrollTop + clientHeight) >= (scrollHeight - NEAR_PX); // true bottom only
        const now = Date.now();
        const sinceLast = now - lastBottomPopupTimeRef.current;
        const authed = typeof isAuthenticated === 'function' ? isAuthenticated() : false;
        if (
          near &&
          !prevNear &&
          !authed &&
          images.length >= 30 &&
          !isPopupVisible &&
          !showEntrancePopup &&
          sinceLast > BOTTOM_POPUP_COOLDOWN_MS
        ) {
          lastBottomPopupTimeRef.current = now;
          setEntrancePopupGateMode(true);
          setShowEntrancePopup(true);
        }
        prevNear = near && !showEntrancePopup; // stay true only if popup not opened
        ticking = false;
      });
    };
    window.addEventListener('scroll', check, { passive: true });
    const gridContainer = document.querySelector('.image-grid-container');
    if (gridContainer) gridContainer.addEventListener('scroll', check, { passive: true });
    return () => {
      window.removeEventListener('scroll', check);
      if (gridContainer) gridContainer.removeEventListener('scroll', check);
    };
  }, [isPopupVisible, showEntrancePopup, images.length, isAuthenticated]);

  // Remove persistent secondary listener; only near-bottom exact check remains

  // Mobile-only: compact the header after the first scroll inside the image grid
  const handleGridScroll = useCallback((e) => {
    if (window.innerWidth > 768) return;
    const el = e.currentTarget;
    // Compact when user has scrolled at least ~24px
    const compact = el.scrollTop > 24;
    if (compact !== isHeaderCompact) {
      setIsHeaderCompact(compact);
    }
  }, [isHeaderCompact]);

  // Update intersection observer on resize for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (observer.current) {
        observer.current.disconnect();
        // Re-observe the last image if it exists
        const lastImage = document.querySelector('.image-container:last-child');
        if (lastImage) {
          // Recreate the observer with current settings
          const isMobile = window.innerWidth <= 768;
          const rootMargin = isMobile ? '50px' : '200px';
          const threshold = isMobile ? 0.5 : 0.01;

          observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) {
              fetchUsersRef.current();
            }
          }, {
            root: null,
            rootMargin,
            threshold,
          });

          observer.current.observe(lastImage);
        }
      }
    };

    // Mobile scroll handler for better infinite scroll (throttled with rAF)
    let scrollTicking = false;
    const handleScroll = () => {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(() => {
        if (window.innerWidth <= 768 && !isLoading && hasMore) {
          const scrollY = window.scrollY;
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;

          // Trigger fetch when user is near bottom (within 100px)
          if (scrollY + windowHeight >= documentHeight - 100) {
            fetchUsersRef.current();
          }
        }
        scrollTicking = false;
      });
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isLoading, hasMore]); // Remove fetchUsers from dependency array

  const showPopup = () => {
    setIsPopupVisible(true);
  }
  const closePopup = () => setIsPopupVisible(false);

  const closePopupOnOutsideClick = (e) => {
    if (e.target.id === 'popup') {
      setIsPopupVisible(false);
    }
  };

  const submitPortrait = async (selectedFile, nickname, email, message) => {

    try {
      const country = selectedCountry;
      const channel = getCurrentChannel(); // Get current channel

      if (!selectedFile || selectedFile.length === 0) {
        showNotification(t('errors.imageRequired'), 'error');
        return;
      }

      const url = portraitUUID ? `/submit/${portraitUUID}` : '/submit';

      const data = await api.post(url, {
        nickname,
        country,
        channel, // Include channel in user creation
        name: '',
        date_of_birth: null,
        email,
        message: message || null,
      }, { isLoadingMain: true, showNotification: true });

      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('uuid', data.portraitUUID);

      await api.post('/upload', formData, { isLoadingMain: true, showNotification: true });

      if (data.step === STEPS.INITIAL_USER_CREATED || data.step === STEPS.INITIAL_IMAGE_CREATED) {
        trackEvent('stripe_redirect_click', { step: data.step });
        await redirectToStripeCheckout(data.portraitUUID, (error) => {
          console.error('Payment redirect failed:', error);
          showNotification('Payment system error. Please try again.', 'error');
        });
      }

      setIsPopupVisible(false);

    } catch (error) {
      console.error('❌ Error Submission error:', error);
    }
  };

  const showNewlyCreatedPortrait = async () => {
    try {
      const userData = await api.get(`/user/${portraitUUID}`, { isLoadingMain: true, showNotification: true });

      // Safely handle date parsing
      let joined = 'Unknown';
      if (userData.created_at) {
        try {
          const date = new Date(userData.created_at);
          if (!isNaN(date.getTime())) {
            joined = date.toLocaleDateString();
          }
        } catch (dateError) {
          console.warn('Invalid date format for created_at:', userData.created_at);
        }
      }

      const imageData = {
        uuid: userData.uuid,
        imageUUID: userData.images?.[0]?.uuid,
        name: userData.nickname || 'Unknown',
        image: userData.images?.[0]?.uuid,
        image_path: userData.images?.[0]?.image_path,
        thumb_path: userData.images?.[0]?.thumb_path || userData.images?.[0]?.image_path, // Prefer thumbnail when available
        nickname: userData.nickname || 'Anonymous',
        country: userData.country || null,
        joined,
      };

      prependImage(imageData);
      showNotification(t('notifications.submissionReceived'), 'success');
    } catch (error) {
      console.error('❌ Error fetching user details:', error);
      window.location.href = '/';
    }
  }

  const handlePortraitClick = async (portrait) => {
    trackEvent('portrait_click', { uuid: portrait.uuid, country: portrait.country || null });
    try {
      //      console.log('🖼️ Portrait click: Making API call to /user/' + portrait.uuid);
      const userDetails = await api.get(`/user/${portrait.uuid}`, { isLoadingMain: true, showNotification: true });
      //      console.log('✅ Portrait click: API response received', userDetails);

      // Safely handle date parsing
      let joined = 'Unknown';
      if (userDetails.created_at) {
        try {
          const date = new Date(userDetails.created_at);
          if (!isNaN(date.getTime())) {
            joined = date.toLocaleDateString();
          }
        } catch (dateError) {
          console.warn('Invalid date format for created_at:', userDetails.created_at);
        }
      }

      // Update image paths in the grid if they've changed
      if (userDetails.images && userDetails.images.length > 0) {
        updateImagePaths(portrait.uuid, userDetails.images);
      }

      setSelectedPortrait({
        ...portrait,
        ...userDetails,
        joined,
      });
    } catch (error) {
      console.error('❌ Error fetching user details:', error);
    }
  };

  const closePortraitDetails = () => {
    setSelectedPortrait(null);
  };

  const closePortraitDetailsOnOutsideClick = (e) => {
    if (e.target.id === 'portrait-popup') {
      setSelectedPortrait(null);
    }
  };

  // Generate a random UUID4
  const generateUUID4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const onParticipateClick = async () => {
    if (portraitUUID && (step === STEPS.INITIAL_USER_CREATED || step === STEPS.INITIAL_IMAGE_CREATED)) {
      fetchUserAndPrefillForm(portraitUUID);
    } else {
      // Direct Stripe redirect with random UUID4 - no API calls
      try {
        const channel = getCurrentChannel();
        const randomUUID = generateUUID4();

        // Track the direct redirect
        trackEvent('direct_stripe_redirect', { channel });

        // Redirect directly to Stripe with random UUID
        await redirectToStripeCheckout(randomUUID, (error) => {
          console.error('Payment redirect failed:', error);
          showNotification('Payment system error. Please try again.', 'error');
        });

      } catch (error) {
        console.error('Error redirecting to payment:', error);
        showNotification('Unable to proceed with payment. Please try again.', 'error');
      }
    }
  }

  const showHowItWorks = () => {
    setIsHowItWorksVisible(true);
  };

  const closeHowItWorks = () => {
    setIsHowItWorksVisible(false);
  };

  const closeHowItWorksOnOutsideClick = (e) => {
    if (e.target.id === 'how-it-works-popup') {
      setIsHowItWorksVisible(false);
    }
  };

  // Determine banner visibility & values (global now)
  const bannerVisible = true; // force on for all channels
  const userCount = (configs?.user_count ?? 63);
  const userLimit = (configs?.user_limit ?? 10000); // large fallback limit

  // Detect legal pages (/terms or /privacy variants) and memory-wall
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const isLegalRoute = /(^|\/)terms(\b|$)/.test(path) || /(^|\/)privacy(\b|$)/.test(path);
  const isMemoryWallRoute = /(^|\/)memory-wall(\b|$)/.test(path);
  const isDashboardRoute = /(^|\/)dashboard(\b|$)/.test(path);

  // Ensure normal scrolling on legal pages and memory wall
  useEffect(() => {
    if (isLegalRoute || isMemoryWallRoute || isDashboardRoute) {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'auto';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
      document.body.style.height = 'auto';
      document.documentElement.style.overflow = 'auto';
    }
  }, [isLegalRoute, isMemoryWallRoute, isDashboardRoute]);

  return (
    <div className={!isLegalRoute && !isMemoryWallRoute && !isDashboardRoute && bannerVisible ? 'has-user-banner' : ''}>
      <Notification
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: '', type: '' })}
      />
      {/* First-visit fixed intro video (bottom-right) - disabled
      {!isLegalRoute && !isMemoryWallRoute && !isDashboardRoute && (
        <React.Suspense fallback={null}>
          {(() => {
            const FixedIntroVideo = require('./components/FixedIntroVideo').default;
            return (
              <FixedIntroVideo src={process.env.PUBLIC_URL + '/videos/WhatsApp Video 2025-10-16 at 8.51.01 PM.mp4'} />
            );
          })()}
        </React.Suspense>
      )}
      */}
      {!isLegalRoute && !isMemoryWallRoute && !isDashboardRoute && (
        <UserLimitBanner
          userCount={userCount}
          userLimit={userLimit}
          isVisible={bannerVisible}
        />
      )}
      {!isLegalRoute && !isMemoryWallRoute && !isDashboardRoute && (
        <PopupModal
          isVisible={isPopupVisible}
          onClose={closePopup}
          onOutsideClick={closePopupOnOutsideClick}
          onSubmit={submitPortrait}
          countryList={countryList}
          selectedCountry={selectedCountry}
          setSelectedCountry={setSelectedCountry}
          prefilledData={prefilledData}
        />
      )}
      {!isLegalRoute && !isMemoryWallRoute && !isDashboardRoute && (
        <HowItWorksModal
          isVisible={isHowItWorksVisible}
          onClose={closeHowItWorks}
          onOutsideClick={closeHowItWorksOnOutsideClick}
        />
      )}

      {!isLegalRoute && !isMemoryWallRoute && !isDashboardRoute && selectedPortrait && (
        <PortraitDetailsModal
          portrait={selectedPortrait}
          onClose={closePortraitDetails}
          onOutsideClick={closePortraitDetailsOnOutsideClick}
        />
      )}
      {isLoadingMain && showMainSpinner && (
        <div className="spinner-container">
          <div className="spinner-main" />
        </div>
      )}
      <div className="App" style={{ overflowX: 'hidden', willChange: 'transform' }}>
        {/* Lazy mount heavy canvas background after first content / idle time */}
        {!isMobile && !isMemoryWallRoute && !isDashboardRoute && <LazyThemeBackground imagesLoaded={images.length > 0} />}
        {!isMobile && !isMemoryWallRoute && !isDashboardRoute && <MemoryRipples />}

        {/* Only show Header if not on Dashboard or Memory Wall */}
        {!isLegalRoute && !isDashboardRoute && !isMemoryWallRoute && (
          <Header
            onParticipateClick={onParticipateClick}
            onHowItWorksClick={showHowItWorks}
            isParticipateShown={isParticipateShown}
            isCompact={isHeaderCompact}
          />
        )}
        <Routes>
          {/* Specific routes MUST come before dynamic routes */}
          {/* Memory Wall - standalone */}
          <Route path="/memory-wall" element={<MemoryWall />} />

          {/* Legal pages (no language prefix) */}
          <Route path="/terms" element={<LegalPage url={'https://ylived-production.up.railway.app/terms'} title={'Terms & Conditions'} />} />
          <Route path="/privacy" element={<LegalPage url={'https://ylived-production.up.railway.app/privacy'} title={'Privacy Policy'} />} />

          {/* Default English */}
          <Route path="/" element={
            <>
              <ImageGrid
                images={images}
                lastImageRef={lastImageRef}
                containerWidth={containerWidth}
                containerHeight={containerHeight}
                onPortraitClick={handlePortraitClick}
                isMobile={isMobile}
                highlightLastTwoRows={highlightGateRows}
                onGridScroll={handleGridScroll}
                // Show the bottom hint only when we've actually reached the end
                // and the user is not authenticated.
                showJoinBottomHint={!isLoading && !hasMore && !isAuthenticated() && images.length > 0}
                joinPrimaryText={t('grid.joinToDiscoverMore')}
                joinSecondaryText={''}
              />
            </>
          } />

          {/* Language-prefixed routes */}
          <Route path=":lang" element={
            <>
              <ImageGrid
                images={images}
                lastImageRef={lastImageRef}
                containerWidth={containerWidth}
                containerHeight={containerHeight}
                onPortraitClick={handlePortraitClick}
                isMobile={isMobile}
                highlightLastTwoRows={highlightGateRows}
                onGridScroll={handleGridScroll}
                showJoinBottomHint={!isLoading && !hasMore && !isAuthenticated() && images.length > 0}
                joinPrimaryText={t('grid.joinToDiscoverMore')}
                joinSecondaryText={''}
              />
            </>
          } />
          {/* Memory Wall with language prefix */}
          <Route path=":lang/memory-wall" element={<MemoryWall />} />
          {/* Legal pages with language prefix */}
          <Route path=":lang/terms" element={<LegalPage url={'https://ylived-production.up.railway.app/terms'} title={'Terms & Conditions'} />} />
          <Route path=":lang/privacy" element={<LegalPage url={'https://ylived-production.up.railway.app/privacy'} title={'Privacy Policy'} />} />
          {/* Channel-specific routes */}
          <Route path=":lang/:channel" element={
            <>
              <ImageGrid
                images={images}
                lastImageRef={lastImageRef}
                containerWidth={containerWidth}
                containerHeight={containerHeight}
                onPortraitClick={handlePortraitClick}
                isMobile={isMobile}
                highlightLastTwoRows={highlightGateRows}
                onGridScroll={handleGridScroll}
                showJoinBottomHint={!isLoading && !hasMore && !isAuthenticated() && images.length > 0}
                joinPrimaryText={t('grid.joinToDiscoverMore')}
                joinSecondaryText={''}
              />
            </>
          } />
          {/* Memory Wall with language and channel */}
          <Route path=":lang/:channel/memory-wall" element={<MemoryWall />} />
          {/* Legal pages with language and channel prefix */}
          <Route path=":lang/:channel/terms" element={<LegalPage url={'https://ylived-production.up.railway.app/terms'} title={'Terms & Conditions'} />} />
          <Route path=":lang/:channel/privacy" element={<LegalPage url={'https://ylived-production.up.railway.app/privacy'} title={'Privacy Policy'} />} />
          <Route path="/payment/redirect" element={
            <ThanksPage
              setIsParticipateShown={setIsParticipateShown}
            />
          } />
          <Route path=":lang/payment/redirect" element={
            <ThanksPage
              setIsParticipateShown={setIsParticipateShown}
            />
          } />
          <Route path=":lang/:theme/payment/redirect" element={
            <ThanksPage
              setIsParticipateShown={setIsParticipateShown}
            />
          } />
          {/* Dashboard routes with language support */}
          <Route path=":lang/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path=":lang/:theme/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
        </Routes>
        {isLoading && images.length > 0 && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div className="spinner" />
            <p style={{ color: '#666', marginTop: '10px' }}>Loading more memories...</p>
          </div>
        )}

        {/* Mobile fallback load more button */}
        {!isLoading && hasMore && window.innerWidth <= 768 && (
          <div style={{ textAlign: 'center', padding: '20px', display: "none" }}>
            <button
              onClick={() => {
                fetchUsersRef.current();
              }}
              style={{
                border: 'none',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'none'
              }}
            >
              Load More Memories
            </button>
          </div>
        )}

        {/* Bottom-of-scroll join text moved inside ImageGrid for proper placement */}
      </div>
      {/* Only show Footer if not on Dashboard or Memory Wall */}
      {!isLegalRoute && !isDashboardRoute && !isMemoryWallRoute && <Footer />}
      {/* Subscribe-to-continue scrolling popup (gate). Shown only on true bottom when gate is active. */}
      {showEntrancePopup && (
        <EntrancePopup
          isVisible={showEntrancePopup}
          onClose={handleEntrancePopupClose}
          canClose={true}
          showLater={true}
          title={t('entrancePopup.gateTitle')}
          description={t('popup.subtitle.line1')}
          subDescription={t('popup.subtitle.line2')}
        />
      )}
    </div>
  );
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<Login />} />
      {/* Admin routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={(<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>)}>
        <Route index element={<AdminProfiles />} />
        <Route path="profiles" element={<AdminProfiles />} />
        <Route path="profiles/:uuid" element={<AdminProfileEditPage />} />
        <Route path="channels" element={<AdminChannels />} />
  <Route path="spot-prices" element={<AdminSpotPrices />} />
        <Route path="payment-links" element={<AdminPaymentLinks />} />
        <Route path="bulk-upload" element={<AdminBulkUpload />} />
        <Route path="profile-fixer" element={<AdminProfileFixer />} />
      </Route>
      {/* Channel + language specific login route */}
      <Route path=":lang/:channel/login" element={<Login />} />
      <Route path=":lang/:channel/reset-password" element={<Login />} />
      <Route path="/*" element={<AppLoader />} />
    </Routes>
  );
}

function AppLoader() {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const api = useCustomFetchApi(() => { });

  useEffect(() => {
    // Establish session before rendering main app to prevent 403 errors
    api.get('/session-state')
      .then(data => {
        // if (data.step) dispatch(setStep(data.step));
        // if (data.portraitUUID) dispatch(setPortraitUUID(data.portraitUUID));
      })
      .catch(err => console.error('❌ Error fetching session state:', err))
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return <AppContent />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AdminAuthProvider>
          <AppRouter />
        </AdminAuthProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}